import { useCallback, useEffect, useState } from "react";
import { getAutoplayType, isFullVideoData, isWapoDomain } from "../../utils";
import { AutoPlayType } from "../../../globals";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { shouldLeadPreroll } from "./utils";
import {
  checkIfVideoTypeLive,
  hasLiveEventEnded,
  isLiveEventUpcoming
} from "../../utils/liveVideoUtils";
import { AUTOPLAY_INIT_VALUE } from "./constants";

/**
 * Determines autoplay settings for initial and subsequent videos
 * Provides function to update settings, esp. autoplay status and playthrough mode
 */
export const useAutoplay = ({
  isLead,
  isMotionReduced,
  isLivePreviewEnabled,
  isAutoplayOverride,
  autoplayLive,
  forceNoLoop,
  isSubscriber
}) => {
  const { videoData } = useVideoDataContext();

  const [autoplayState, setAutoplayState] = useState(AUTOPLAY_INIT_VALUE);

  const updateAutoplayState = useCallback(
    (updatedState, overrideReducedMotion = false) => {
      if (isMotionReduced && !overrideReducedMotion) return;
      setAutoplayState(previousState => ({
        ...previousState,
        ...updatedState
      }));
    },
    [isMotionReduced]
  );

  useEffect(() => {
    if (
      !videoData ||
      !isFullVideoData(videoData) ||
      // Wait until reduced motion preference is set and ready to use!
      isMotionReduced === undefined
    ) {
      return;
    }

    // If we're offsite or a user has reduced motion we override all autoplay behavior
    if (isMotionReduced || isLiveEventUpcoming(videoData) || !isWapoDomain()) {
      setAutoplayState({
        ...AUTOPLAY_INIT_VALUE,
        isInitialized: true
      });
      return;
    }

    let type = getAutoplayType(videoData, forceNoLoop);

    const isLooping = [
      AutoPlayType.LOOP,
      AutoPlayType.LOOP_NO_CONTROLS
    ].includes(type);

    let isPreroll = false;

    if (!isLooping && shouldLeadPreroll(isLead, isSubscriber, videoData)) {
      isPreroll = true;
      type = AutoPlayType.AUTO;
    }

    let isLivePreview = false;

    if (
      isLivePreviewEnabled &&
      !hasLiveEventEnded(videoData) &&
      !isLiveEventUpcoming(videoData) &&
      checkIfVideoTypeLive(videoData)
    ) {
      isLivePreview = true;
      type = AutoPlayType.AUTO;
    }

    let overrideAutoplayMuteBehavior = false;

    if (checkIfVideoTypeLive(videoData) && autoplayLive) {
      type = AutoPlayType.AUTO;
      overrideAutoplayMuteBehavior = true;
    }

    if (isAutoplayOverride) {
      type = AutoPlayType.AUTO;
    }

    const isAutoplay =
      isAutoplayOverride ||
      (checkIfVideoTypeLive(videoData) && autoplayLive) ||
      !!type;

    if (isAutoplay && !isLooping && !isSubscriber && !isLivePreview) {
      isPreroll = true;
    }

    updateAutoplayState({
      type,
      isLooping,
      isAutoplay,
      isPreroll,
      isLivePreview,
      overrideAutoplayMuteBehavior,
      isInitialized: true
    });
  }, [
    isMotionReduced,
    videoData,
    isLead,
    isLivePreviewEnabled,
    updateAutoplayState,
    isAutoplayOverride,
    forceNoLoop,
    autoplayLive,
    isSubscriber
  ]);

  return { autoplayState, updateAutoplayState };
};
